import React, { useEffect, useRef, useState } from "react";
import "../../VideoManager.css";
import { ensureMandatoryTag } from "../../utils/videoManagerUtils";
import uploadVideoEndpoint from "../../config/uploadApi";

const VISUAL_BACKGROUNDS = [
  { id: "nebula", label: "Nebula glow" },
  { id: "dusk", label: "Dusk gradient" },
  { id: "aurora", label: "Aurora shimmer" },
];

const BACKGROUND_PATTERNS = {
  nebula: "radial-gradient(circle at 20% 20%, rgba(255, 255, 255, 0.2), rgba(32, 8, 55, 0.9) 70%)",
  dusk: "linear-gradient(160deg, #33304c, #09112d)",
  aurora: "linear-gradient(180deg, #08192d 0%, #0b3d53 50%, #3ec5c5 100%)",
};

const sanitizeFileName = (value = "") =>
  value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/(^-|-$)/g, "") || "audio-vision";

export default function AudioEditingStudio() {
  const [audioFile, setAudioFile] = useState(null);
  const [audioPreviewUrl, setAudioPreviewUrl] = useState("");
  const [fadeIn, setFadeIn] = useState(1);
  const [fadeOut, setFadeOut] = useState(1);
  const [equalizer, setEqualizer] = useState("warm");
  const [backgroundStyle, setBackgroundStyle] = useState(VISUAL_BACKGROUNDS[0].id);
  const [convertedVideoNote, setConvertedVideoNote] = useState("");
  const [convertedVideoReady, setConvertedVideoReady] = useState(false);
  const [showAudioVideoPreview, setShowAudioVideoPreview] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("Audio Vision Mix");
  const [uploadDescription, setUploadDescription] = useState(
    "Audio-focused session converted into a guided visual for worship.",
  );
  const [tags, setTags] = useState(ensureMandatoryTag(["audio-edit", "visuals"]));
  const [tagInput, setTagInput] = useState("");
  const [uploadStatus, setUploadStatus] = useState("");
  const [isAudioUploading, setIsAudioUploading] = useState(false);
  const [audioUploadLink, setAudioUploadLink] = useState("");
  const [convertedVideoBlob, setConvertedVideoBlob] = useState(null);
  const [convertedVideoUrl, setConvertedVideoUrl] = useState("");
  const [isConverting, setIsConverting] = useState(false);
  const [customOverlayText, setCustomOverlayText] = useState("Audio Vision Mix");
  const [useAudioTitleAsOverlay, setUseAudioTitleAsOverlay] = useState(true);

  const audioPreviewRef = useRef("");
  const animationFrameRef = useRef(null);
  const recorderRef = useRef(null);
  const audioElementRef = useRef(null);
  const canvasRef = useRef(null);
  const convertedVideoUrlRef = useRef("");
  const backgroundLabel =
    VISUAL_BACKGROUNDS.find((style) => style.id === backgroundStyle)?.label || "custom background";

  useEffect(() => {
    return () => {
      if (audioPreviewRef.current) {
        URL.revokeObjectURL(audioPreviewRef.current);
      }
      if (convertedVideoUrlRef.current) {
        URL.revokeObjectURL(convertedVideoUrlRef.current);
      }
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (recorderRef.current && recorderRef.current.state === "recording") {
        recorderRef.current.stop();
      }
      if (audioElementRef.current) {
        audioElementRef.current.pause();
      }
      if (canvasRef.current) {
        canvasRef.current.getContext("2d")?.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      }
    };
  }, []);

  useEffect(() => {
    if (!convertedVideoReady) {
      setShowAudioVideoPreview(false);
    }
  }, [convertedVideoReady]);

  const handleAudioUpload = (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setAudioFile(file);
    setConvertedVideoReady(false);
    setConvertedVideoNote("");
    if (audioPreviewRef.current) {
      URL.revokeObjectURL(audioPreviewRef.current);
    }
    audioPreviewRef.current = URL.createObjectURL(file);
    setAudioPreviewUrl(audioPreviewRef.current);
    setCustomOverlayText(file.name.replace(/\.[^.]+$/, ""));
    setUseAudioTitleAsOverlay(true);
    setUploadTitle(file.name.replace(/\.[^.]+$/, ""));
    if (convertedVideoUrlRef.current) {
      URL.revokeObjectURL(convertedVideoUrlRef.current);
      convertedVideoUrlRef.current = "";
      setConvertedVideoUrl("");
      setConvertedVideoBlob(null);
    }
  };

  const handleConvert = async () => {
    if (!audioFile) {
      setConvertedVideoNote("Upload an audio file before building the video background.");
      return;
    }
    if (!audioPreviewUrl) {
      setConvertedVideoNote("Preview the audio first so we can sync the visuals.");
      return;
    }
    if (convertedVideoUrlRef.current) {
      URL.revokeObjectURL(convertedVideoUrlRef.current);
      convertedVideoUrlRef.current = "";
    }
    setConvertedVideoBlob(null);
    setConvertedVideoUrl("");
    setConvertedVideoReady(false);
    setConvertedVideoNote("Generating visual background and syncing audio…");
    setIsConverting(true);

    const canvas = document.createElement("canvas");
    canvas.width = 1280;
    canvas.height = 720;
    canvasRef.current = canvas;
    const ctx = canvas.getContext("2d");

    const overlayTitle =
      (useAudioTitleAsOverlay && audioFile?.name
        ? audioFile.name.replace(/\.[^.]+$/, "")
        : customOverlayText) || "Audio Vision Mix";
    const wrapOverlayText = (value, maxWidth) => {
      if (!value) return [""];
      const words = value.split(" ");
      const lines = [];
      let currentLine = "";
      words.forEach((word) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        const metrics = ctx.measureText(testLine);
        if (metrics.width > maxWidth && currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          currentLine = testLine;
        }
      });
      if (currentLine) {
        lines.push(currentLine);
      }
      return lines;
    };
    const drawFrame = (timestamp = 0) => {
      const hue = ((timestamp / 40) % 360);
      const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
      gradient.addColorStop(0, `hsl(${hue}, 65%, 45%)`);
      gradient.addColorStop(1, `hsl(${(hue + 70) % 360}, 75%, 25%)`);
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      ctx.fillStyle = "rgba(255,255,255,0.45)";
      const baseY = canvas.height - 60;
      for (let i = 0; i < 20; i += 1) {
        const amplitude = Math.abs(Math.sin((timestamp / 400) + i)) * 40 + 15;
        const barWidth = canvas.width / 20 - 6;
        const x = i * (barWidth + 6) + 20;
        ctx.fillRect(x, baseY - amplitude, barWidth, amplitude);
      }

      ctx.font = "700 52px Inter, sans-serif";
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      const overlayLines = wrapOverlayText(overlayTitle, canvas.width * 0.8);
      const lineHeight = 54;
      const startY = canvas.height / 2 - ((overlayLines.length - 1) * lineHeight) / 2;
      overlayLines.forEach((line, index) => {
        ctx.fillText(line, canvas.width / 2, startY + index * lineHeight);
      });

      animationFrameRef.current = requestAnimationFrame(drawFrame);
    };

    animationFrameRef.current = requestAnimationFrame(drawFrame);

    const videoStream = canvas.captureStream(30);
    const audioEl = new Audio(audioPreviewUrl);
    audioEl.crossOrigin = "anonymous";
    audioElementRef.current = audioEl;
    try {
      await audioEl.play();
    } catch (error) {
      setConvertedVideoNote(
        "Playback blocked. Interact with the page and try again so we can capture the audio.",
      );
      setIsConverting(false);
      cancelAnimationFrame(animationFrameRef.current);
      return;
    }
    const captureFn = audioEl.captureStream || audioEl.mozCaptureStream;
    const audioStream = captureFn ? captureFn.call(audioEl) : null;
    if (!audioStream) {
      setConvertedVideoNote("Unable to capture audio from this browser.");
      setIsConverting(false);
      audioEl.pause();
      cancelAnimationFrame(animationFrameRef.current);
      return;
    }

    const mixedStream = new MediaStream([
      ...videoStream.getVideoTracks(),
      ...audioStream.getAudioTracks(),
    ]);
    const recorder = new MediaRecorder(mixedStream, { mimeType: "video/webm;codecs=vp9" });
    recorderRef.current = recorder;
    const chunks = [];
    recorder.ondataavailable = (event) => {
      if (event.data && event.data.size) {
        chunks.push(event.data);
      }
    };
    recorder.onstop = () => {
      const blob = new Blob(chunks, { type: "video/webm" });
      const url = URL.createObjectURL(blob);
      convertedVideoUrlRef.current = url;
      setConvertedVideoBlob(blob);
      setConvertedVideoUrl(url);
      setConvertedVideoReady(true);
      setConvertedVideoNote(
        `Converted ${audioFile.name} into a cinematic video with ${backgroundLabel}.`,
      );
      setShowAudioVideoPreview(true);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      audioEl.pause();
      videoStream.getVideoTracks().forEach((track) => track.stop());
      audioStream.getTracks().forEach((track) => track.stop());
      setIsConverting(false);
    };
    recorder.start();

    audioEl.addEventListener("ended", () => {
      if (recorder.state === "recording") {
        recorder.stop();
      }
    });

    setConvertedVideoNote("Recording the audio-driven visuals…");
    setConvertedVideoReady(false);
  };

  const handleAddTag = () => {
    const candidate = tagInput.trim();
    setTagInput("");
    if (!candidate) return;
    setTags((state) => ensureMandatoryTag([...state, candidate]));
  };

  const handleUploadRequest = async () => {
    if (!convertedVideoReady || !convertedVideoBlob) {
      setUploadStatus("Convert the audio into a video before queuing a YouTube upload.");
      return;
    }
    setIsAudioUploading(true);
    setAudioUploadLink("");
    setUploadStatus("Uploading the audio-visual mix to @gcc-worshop…");
    const normalizedTags = ensureMandatoryTag(tags);
    const formData = new FormData();
    formData.append("video", convertedVideoBlob, `${uploadTitle
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "") || "audio-vision"}.webm`);
    formData.append("title", uploadTitle);
    formData.append(
      "description",
      `${uploadDescription}\nFade in: ${fadeIn}s · Fade out: ${fadeOut}s · EQ: ${equalizer}`,
    );
    formData.append("privacy", "unlisted");
    formData.append("tags", JSON.stringify(normalizedTags));

    try {
      const res = await fetch(uploadVideoEndpoint, {
        method: "POST",
        body: formData,
      });
      if (!res.ok) {
        const body = await res.text();
        throw new Error(body || "Upload backend rejected the file.");
      }
      const payload = await res.json();
      setAudioUploadLink(payload.videoUrl);
      setUploadStatus(`Upload complete. Watch ${payload.videoUrl}`);
    } catch (error) {
      console.error("Audio video upload failed", error);
      setUploadStatus(`Upload failed: ${error.message || "Server error"}`);
    } finally {
      setIsAudioUploading(false);
    }
  };

  const downloadFileName = `${sanitizeFileName(uploadTitle)}.webm`;

  return (
    <section className="studio-panel">
      <div className="studio-panel__dual-grid audio-lab__layout">
        <div className="studio-panel__controls-column">
          <div className="studio-panel__control-row">
            <label className="studio-panel__file-control">
              <span>Upload audio</span>
              <input type="file" accept="audio/*" onChange={handleAudioUpload} />
            </label>
            <span className="studio-panel__status">
              {audioFile ? `Loaded ${audioFile.name}` : "No audio loaded yet."}
            </span>
          </div>

          <div className="studio-panel__options">
            <label>
              <span>Fade in (seconds)</span>
              <input
                type="range"
                min={0}
                max={8}
                step={0.5}
                value={fadeIn}
                onChange={(event) => setFadeIn(Number(event.target.value))}
              />
              <small>{fadeIn}s</small>
            </label>
            <label>
              <span>Fade out (seconds)</span>
              <input
                type="range"
                min={0}
                max={8}
                step={0.5}
                value={fadeOut}
                onChange={(event) => setFadeOut(Number(event.target.value))}
              />
              <small>{fadeOut}s</small>
            </label>
            <label>
              <span>EQ preset</span>
              <select value={equalizer} onChange={(event) => setEqualizer(event.target.value)}>
                <option value="warm">Warm bass</option>
                <option value="bright">Bright vocals</option>
                <option value="balanced">Balanced</option>
                <option value="vocal-cut">Vocal removal</option>
              </select>
            </label>
          </div>

          <div className="studio-panel__overlay-options">
            <label>
              <span>Overlay text</span>
              <input value={customOverlayText} onChange={(event) => setCustomOverlayText(event.target.value)} />
            </label>
            <label className="studio-panel__checkbox-label">
              <input
                type="checkbox"
                checked={useAudioTitleAsOverlay}
                onChange={(event) => setUseAudioTitleAsOverlay(event.target.checked)}
              />
              <span>Use audio file name as overlay</span>
            </label>
          </div>

          <div className="studio-panel__karaoke">
            <div className="studio-panel__karaoke-header">
              <h3>Background video builder</h3>
              <p>Choose a visual theme and convert the edited audio into a shareable video.</p>
            </div>
            <div className="studio-panel__karaoke-modes">
              {VISUAL_BACKGROUNDS.map((style) => (
                <label key={style.id}>
                  <input
                    type="radio"
                    name="backgroundStyle"
                    value={style.id}
                    checked={backgroundStyle === style.id}
                    onChange={(event) => setBackgroundStyle(event.target.value)}
                  />
                  <span>{style.label}</span>
                </label>
              ))}
            </div>
            <button
              type="button"
              className="studio-panel__cta"
              onClick={handleConvert}
              disabled={isConverting}
            >
              {isConverting ? "Recording visuals…" : convertedVideoReady ? "Regenerate video" : "Convert to video"}
            </button>
          </div>
        </div>

        <div className="studio-panel__preview-column">
          {audioPreviewUrl && (
            <div className="studio-panel__audio-preview-block">
              <audio controls src={audioPreviewUrl} className="studio-panel__audio-preview" />
              {audioFile && (
                <div className="studio-panel__preview-actions">
                  <a
                    className="studio-panel__cta studio-panel__cta--secondary"
                    href={audioPreviewUrl}
                    download={audioFile.name}
                  >
                    Download uploaded audio
                  </a>
                </div>
              )}
            </div>
          )}

          <div className="studio-panel__preview-actions">
            <button
              type="button"
              className="studio-panel__cta"
              onClick={() => setShowAudioVideoPreview((prev) => !prev)}
              disabled={!convertedVideoReady}
            >
              {showAudioVideoPreview ? "Hide preview" : "Preview converted video"}
            </button>
          </div>
          {convertedVideoNote && <p className="studio-panel__helper">{convertedVideoNote}</p>}

          {showAudioVideoPreview && convertedVideoReady && convertedVideoUrl && (
            <div
              className="recording-studio__preview-card"
              style={{
                background: BACKGROUND_PATTERNS[backgroundStyle] || BACKGROUND_PATTERNS.nebula,
              }}
            >
              <div className="recording-studio__label">Converted video preview</div>
              <video
                controls
                src={convertedVideoUrl}
                className="studio-panel__video-preview"
                playsInline
              />
              <p className="studio-panel__preview-title">{uploadTitle || "Untitled mix"}</p>
              <small>
                Fade in {fadeIn}s · Fade out {fadeOut}s · EQ: {equalizer}
              </small>
              {convertedVideoBlob && (
                <a
                  className="studio-panel__cta studio-panel__cta--secondary"
                  href={convertedVideoUrl}
                  download={downloadFileName}
                >
                  Download regenerated video
                </a>
              )}
            </div>
          )}

          <form
            className="studio-panel__form"
            onSubmit={(event) => {
              event.preventDefault();
              handleUploadRequest();
            }}
          >
            <label>
              <span>Title</span>
              <input value={uploadTitle} onChange={(event) => setUploadTitle(event.target.value)} />
            </label>
            <label>
              <span>Description</span>
              <textarea
                rows={3}
                value={uploadDescription}
                onChange={(event) => setUploadDescription(event.target.value)}
              />
            </label>
            <label>
              <span>Tags</span>
              <div className="studio-panel__tag-row">
                <input
                  placeholder="Add tags (e.g., inspiration, live)"
                  value={tagInput}
                  onChange={(event) => setTagInput(event.target.value)}
                />
                <button type="button" onClick={handleAddTag}>
                  Add tag
                </button>
              </div>
            </label>
            <div className="studio-panel__tag-list">
              {ensureMandatoryTag(tags).map((tag) => (
                <span key={tag} className="studio-panel__tag">
                  {tag}
                </span>
              ))}
            </div>
            <button
              type="submit"
              className="studio-panel__cta"
              disabled={isAudioUploading}
            >
              {isAudioUploading ? "Uploading…" : "Upload audio-converted video to @gcc-worshop"}
            </button>
            {uploadStatus && <p className="studio-panel__helper">{uploadStatus}</p>}
            {audioUploadLink && (
              <a className="studio-panel__helper" href={audioUploadLink} target="_blank" rel="noreferrer">
                View uploaded mix
              </a>
            )}
          </form>
        </div>
      </div>
    </section>
  );
}
